import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import 'lord-icon-element';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Close as CloseIcon } from '@mui/icons-material';

import { useDeleteUserModalStore } from './useDeleteUserModalStore';
import AnimatedIcon from './Icon';

import { MLButton } from 'components/Button/Button';
import useDeleteUserPage from 'hooks/Users/useDeleteUserPage';

interface Props {
  refetchUsers: () => void;
  refetchClientUsers: () => void;
}

const DeleteUserModal: React.FC<Props> = ({ refetchUsers, refetchClientUsers }) => {
  const { isOpen, onDeleteUserModalClose, userData } = useDeleteUserModalStore();
  const { deleteUserPage, isLoading } = useDeleteUserPage();
  const { t } = useTranslation();

  const handleSubmit = () => {
    if (!userData?.id) {
      enqueueSnackbar(t('DeleteModal.error-no-user'), { variant: 'error' });
      return;
    }

    deleteUserPage(userData.id, {
      onSuccess: () => {
        enqueueSnackbar(
          t('DeleteModal.success-deleted', {
            firstName: userData?.first_name,
            lastName: userData?.last_name,
          }),
          {
            variant: 'success',
            action: key => (
              <IconButton onClick={() => closeSnackbar(key)} color="inherit">
                <CloseIcon />
              </IconButton>
            ),
          },
        );
        refetchUsers();
        refetchClientUsers();
        onDeleteUserModalClose();
      },
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={onDeleteUserModalClose} size="md">
      <ModalHeader className="border-bottom pb-2 pt-3" toggle={onDeleteUserModalClose}>
        <div className="text-purple">{t('DeleteModal.delete-title')}</div>
      </ModalHeader>
      <ModalBody>
        <div className="row mb-3">
          <div className="col-xl-12 text-center">
            <AnimatedIcon />
          </div>
        </div>
        <div className="row mb-5">
          <div className="col-xl-12 text-center">
            <h5 className="text-muted font- my-3">
              <em>{t('DeleteModal.are-you-sure')}</em>
            </h5>
            <h4 className="text-danger">
              {userData?.first_name} {userData?.last_name}
            </h4>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <MLButton
          onClick={() => {
            onDeleteUserModalClose();
          }}
          color="danger"
          innerText={t('DeleteModal.cancel')}
          classNames="mx-1"
        />
        <MLButton
          onClick={() => handleSubmit()}
          isGhost
          color="success"
          innerText={t('DeleteModal.confirm')}
          disabled={isLoading}
          isCustomIcon
          icon={
            isLoading ? (
              <span className="spinner-border flex-shrink-0" role="status">
                <span className="visually-hidden">{t('DeleteModal.loading')}</span>
              </span>
            ) : null
          }
        />
      </ModalFooter>
    </Modal>
  );
};

export default DeleteUserModal;
